import Checkbox from '@atlaskit/checkbox';
import {
  getDatabaseContainerType,
  getSelectionsAnalyticsAttributes,
  useAnalytics
} from '@databases/analytics';
import { entryCheckboxMetric } from '@databases/frontend-metrics/metrics/entry-checkbox';
import { useMetric } from '@databases/frontend-metrics/use-metric';
import { isModifierKeyPressed } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { calculateRangeFromFocus } from '@databases/redux/cellSelections/utils';
import { getDatabaseWithCurrentView } from '@databases/redux/database-view';
import { getService } from '@databases/service-container';
import React, { useCallback } from 'react';

const EntryCheckbox = ({
  contentId,
  entryId,
  isSelected,
  referencing,
  index
}: {
  contentId: string;
  entryId: string;
  isSelected: boolean;
  referencing: boolean;
  index: number;
}) => {
  const analyticsClient = useAnalytics();
  const store = defaultStoreUtils.useStore();
  const selectionsActions = getService('SelectionsActions');
  const databaseContainer = getDatabaseContainerType();
  const { fire } = useMetric(entryCheckboxMetric);

  const handleRowCheckboxSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const startTime = performance.now();
      const tableRoot = (e.target as HTMLElement).closest('table');
      e.target.blur();
      if (!entryId) {
        return;
      }

      const reader = getService('SelectionsReader');
      const isRangeSelectOn =
        isModifierKeyPressed(e.nativeEvent as MouseEvent | KeyboardEvent, ['shiftKey']) &&
        !isSelected;

      analyticsClient.sendRowCheckboxSelectedEvent({
        entryId,
        contentId,
        databaseContainer,
        checkboxState: isSelected ? 'unselected' : 'selected',
        ...getSelectionsAnalyticsAttributes(contentId)
      });

      if (isRangeSelectOn) {
        const focusedItem = reader.getFocusedItem();
        const database = getDatabaseWithCurrentView(store.getState(), { contentId });
        const entries = database?.entries ?? [];
        const focusedEntryId =
          focusedItem && 'entryId' in focusedItem ? focusedItem.entryId : undefined;
        const rangeEntryIds = calculateRangeFromFocus(entries, focusedEntryId, entryId);

        const expectedSelectedEntryIds = new Set(reader.getSelectedEntryIds());
        rangeEntryIds.forEach((id) => expectedSelectedEntryIds.add(id));

        selectionsActions.selectEntries(rangeEntryIds, { clear: false });

        fire({
          startTime,
          probeData: {
            tableRoot,
            probe: { kind: 'selection-set', expectedSelectedEntryIds }
          },
          metadata: {
            contentId,
            isShiftRangeSelect: true
          },
          ...(!tableRoot && {
            failImmediately: { reason: 'entry-checkbox-dom-sync-missing-table-root' }
          })
        });
      } else {
        const expectRowCheckboxChecked = !reader.isEntrySelected(entryId);

        if (!expectRowCheckboxChecked) {
          const currentEntries = reader.getSelectedEntryIds();
          const remainingEntries = currentEntries.filter((id) => id !== entryId);
          if (remainingEntries.length > 0) {
            selectionsActions.selectEntries(remainingEntries, {
              clearSelectedValues: false
            });
          } else {
            selectionsActions.deselectAll();
          }
        } else {
          selectionsActions.selectEntries([entryId], { clear: false });
        }

        fire({
          startTime,
          probeData: {
            tableRoot,
            probe: { kind: 'single-row', entryId, expectRowCheckboxChecked }
          },
          metadata: {
            contentId,
            isShiftRangeSelect: false
          },
          ...(!tableRoot && {
            failImmediately: { reason: 'entry-checkbox-dom-sync-missing-table-root' }
          })
        });
      }
      selectionsActions.clearSelectionIntent();
    },
    [
      isSelected,
      entryId,
      analyticsClient,
      contentId,
      databaseContainer,
      store,
      selectionsActions,
      fire
    ]
  );

  return (
    <Checkbox
      onChange={handleRowCheckboxSelected}
      isChecked={isSelected}
      isDisabled={referencing}
      tabIndex={-1}
      className="row-checkbox"
      data-entry-id={entryId}
      data-row-index={index}
    />
  );
};
export default EntryCheckbox;
